import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClearIcon from '@mui/icons-material/Clear';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {formatCurrency} from '../../helpers/currency';
import {useUpdateSessionMutation} from '../../redux/services/sessionApi';
import {getCancelReasons} from '../../helpers/selectOptions';
import FormInput from '../common/FormInput';

const getFields = ({} = {}) => ([
  {
    name: 'cancel_note',
    label: 'Note',
    type: 'textarea',
    multiline: true,
    rows: 3,
    placeholder: 'Optional note',
    visibleToPatient: true,
    fullWidth: true,
    grid: {xs: 12, md: 12},
  },
  {
    name: 'cancel_reason',
    label: 'Reason for cancellation',
    type: 'select',
    options: getCancelReasons(),
    placeholder: 'Select a reason',
    visibleToPatient: false,
    fullWidth: true,
    grid: {xs: 12, md: 6},
  },
  {
    name: 'cancellation_fee',
    label: 'Cancellation Fee',
    type: 'currency',
    placeholder: 'Cancellation Fee',
    visibleToPatient: false,
    defaultValue: 0.00,
    grid: {xs: 12, md: 3},
  },
  {
    name: 'is_no_show',
    label: 'No show',
    type: 'boolean',
    defaultValue: false,
    visibleToPatient: false,
    grid: {xs: 12, md: 3},
    style: {marginTop: '1.5rem'},
  },
  {
    name: 'send_notification',
    label: 'Update calendars',
    helperText: 'Send updated calendar invite to patient and provider',
    type: 'boolean',
    defaultValue: true,
    visibleToPatient: false,
    grid: {xs: 12, md: 6},
    style: {marginTop: '1.5rem'},
  },
]);

const handleSubmit = ({
  updateSession,
  sessionId,
  setDialogIsOpen,
}) => async formData => {
  let body = {is_canceled: true};
  if (formData.is_no_show) body.is_no_show = true;
  if (formData.cancel_note) body.cancel_note = formData.cancel_note;
  if (formData.cancellation_fee) body.cancellation_fee = formData.cancellation_fee;
  if (formData.cancel_reason) body.cancel_reason = formData.cancel_reason;
  if (isBoolean(formData.send_notification)) {
    body.send_notification = formData.send_notification;
  }
  console.log('body', body);
  const req = await updateSession({id: sessionId, body});
  if (req.data && !req.error) return true; 
  return false;
};

const handleChange = ({setFormData, formData}) => name => (e, val) => {
  const value = val !== undefined ? val : e?.target?.value;
  console.log('handleChange', name, value);
  setFormData({
    ...formData,
    [name]: value,
  });
};

const CancelSessionDialog = props => {
  const {
    session, 
    isPatient, 
    useDialog = true,
    onReschedule,
  } = props;
  const [formData, setFormData] = useState({
    cancel_note: '',
    cancel_reason: null,
    cancellaton_fee: 0,
    is_no_show: false,
    send_notification: true,
  });
  const [dialogIsOpen, setDialogIsOpen] = useState();
  const [updateSession, response] = useUpdateSessionMutation();
  const navigate = useNavigate();
  const onSubmit = handleSubmit({
    updateSession,
    sessionId: get(session, 'id'),
    setDialogIsOpen,
  });
  const fields = getFields({})
    .filter(f => isPatient ? f.visibleToPatient : true)
    .filter(f => 
      f.name !== 'cancellation_fee' 
      || session?.case?.billing_category !== 'DIRECT_BILL'
    );
  const DialogWrapper = useDialog ? Dialog : Box;
  return (
    <>
      {useDialog && <Button
        size={props.size}
        variant={props.variant}
        disabled={
          session.is_canceled
          || session.is_completed
          || (response.data && !response.error)
        }
        onClick={() => setDialogIsOpen(true)}
      >
        <ClearIcon />
        {' '}
        Cancel Session
      </Button>}
      <DialogWrapper
       open={dialogIsOpen}
       onClose={() => setDialogIsOpen(false)}
       fullWidth={true}
       maxWidth='sm'
       aria-labelledby="alert-dialog-title"
       aria-describedby="alert-dialog-description"
      >
        <DialogContent sx={{padding: '0'}}>
          {useDialog && <Typography variant='h1' padding={2}>
            Cancel Session
          </Typography>}
          {useDialog && <Divider/>}
          <Stack spacing={1} padding={2}>
            {get(response, 'error')
              ? <Alert severity='error'>
                There was an error processing your request. Please try again later.
              </Alert>
              : null
            }
            <Grid container fullWidth>
              {
                fields.map((f, i) => (
                  <Grid item key={i} xs={f.grid?.xs || 12} md={f.grid?.md || 12} padding={1}>
                    <FormInput 
                      {...f} 
                      onChange={handleChange({setFormData, formData})(f.name)}
                    />
                  </Grid>
                ))
              }
            </Grid>
            {
              isPatient &&
              <Typography variant='body1'>
                  A quick reminder: We have a 24-hour cancellation policy, if you are cancelling the session 
                  within 24 hours of your appointment, you may be charged in full. We are happy to accomodate 
                  a virtual visit in the case of illness or travel.
              </Typography>
            }
            {!!formData.cancellation_fee &&
              <Box style={{borderRadius: '4px', backgroundColor: '#F3F3F3', padding: '8px'}}>
                <Typography variant='body1' style={{color: 'black', }}>
                  <strong>Cancellation Fee</strong>: {formatCurrency(formData.cancellation_fee)}
                </Typography>
              </Box>
            }
          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack paddingBottom={1} className='w-100' spacing={2} direction="row" useFlexGap flexWrap='wrap' justifyContent='center' >
            {useDialog && <Button 
              variant='primaryOutline' 
              onClick={useDialog 
                ? () => setDialogIsOpen(false)
                : () => props.onClose()
              }>
              Close
            </Button>}
            <Button
              variant='primaryOutline'
              color='error'
              onClick={async () => {
                const success = await onSubmit(formData);
                if (success) {
                  if (useDialog) setDialogIsOpen(false);
                  if (props.onClose) props.onClose();
                }
              }}
            >
              Cancel Session
            </Button>
            <Button
              variant='primaryGradient'
              color='error'
              onClick={async () => {
                const success = await onSubmit(formData);
                if (success) {
                  if (onReschedule) {
                    onReschedule();
                  } else {
                    navigate(
                      isPatient
                      ? `/clients/appointments/new/${session.provider.id}`
                      : `/providers/appointments/patients/${session.patient.id}`
                    );
                  }
                }
              }}
            >
              Cancel & Reschedule
            </Button>
          </Stack>
        </DialogActions>
      </DialogWrapper>
    </>
  );
};

export default CancelSessionDialog;
